import {
  DownOutlined,
  EditOutlined,
  FilterFilled,
  MoreOutlined,
} from "@ant-design/icons";
import {
  BRIEF_INFLUENCER_V2_SEARCH_QUERY_KEY,
  BreifInfluencerV2StatusEnum,
  SORT_ORDER,
} from "@entities/brief/lib/constants";
import { useEditDataFieldV2Data } from "@entities/brief/lib/hooks/useEditDataFieldV2Data";
import { useGetBrIefById } from "@entities/brief/lib/hooks/useGetBriefById";
import { NotesCell } from "@entities/brief/ui/NotesCell/NotesCell";
import { useGetDynamicFacets } from "@entities/campaigns/lib/hooks";
import PricingPocNotes from "@entities/PricingProject/ui/PricingPocNotes";

import useGetDeliverableConfigurators from "@entities/deliverableConfigurator/lib/hooks/useGetDeliverableConfigurators";
import { BriefCreatorColumn } from "@features/breifCreatorColumn/BriefCreatorColumn";
import BriefContactFilterV2 from "@features/BriefContactFilter/BriefContactFilterV2";
import { WHATSAPP_REACHOUT_STATUS_FILTER_OPTIONS } from "@features/PricingProject/lib/whatsappReachoutStatus";
import { PricingDeliverableFilter } from "@features/PricingProject/ui/PricingDeliverableFilter/PricingDeliverableFilter";
import { PricingCreatorCommercialsCell } from "@features/PricingProject/ui/PricingCreatorCommercialsCell";
import { PricingReachoutCell } from "@features/PricingProject/ui/PricingReachoutCell/PricingReachoutCell";
import {
  CustomFieldType,
  IBriefInfluencerV2,
  IDataFieldValue,
  IFieldConfig,
} from "@shared/lib/types/briefInfluencerV2";
import { formatData } from "@shared/lib/utils/formatData";
import { CreatorContactDetails, InrAmountWordsLine } from "@shared/ui";
import { ColumnSortUI } from "@shared/ui/filters/ColumnSortUI";
import DateRangeFilter from "@shared/ui/filters/DateRangeFilter";
import NumberRangeFilter from "@shared/ui/filters/NumberRangeFilter";
import { SearchMultiSelectFilter } from "@shared/ui/filters/SearchMultiSelectFilter";
import { SearchSingleSelectFilter } from "@shared/ui/filters/SearchSingleSelectFilter";
import SelectFilter from "@shared/ui/filters/SelectFilter";
import { Close } from "@shared/ui/icons";
import InputNumber from "@shared/ui/InputNumber";
import { SelectButton } from "@shared/ui/Select";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import { getDynamicColumnSizing } from "@widgets/CampaignBrief/lib/getDynamicColumnSizing";
import { BriefDeliverablePill } from "@widgets/CampaignBrief/ui/CampaignBriefTable/BriefDeliverablePill";
import {
  UploadCell,
  UploadItem,
} from "@widgets/CampaignBrief/ui/CampaignBriefTable/UploadCell";
import { ColumnFilterWrapper } from "@shared/ui/filters/ColumnFilterWrapper";
import {
  Button,
  Dropdown,
  Flex,
  Input,
  List,
  Popover,
  Radio,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { format } from "date-fns";
import dayjs from "dayjs";
import { Check } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import styles from "../ui/PricingProject.module.scss";

const { Text } = Typography;

export const usePricingProjectColumns = ({
  briefId,
  userId,
  selectedInfluencer,
  setSelectedInfluencer,
  setSocialMediaInfluencer,
  setContactsModalProps,
  openPocForm,
  setOpenPocForm,
  influencerCampaignHistory,
  isFetchingInfluencerCampaignHistory,
  queryClient,
  reachoutStatusFilter,
  setReachoutStatusFilter,
  contactFilter,
  setContactFilter,
  isContactFilterActive,
  deliverableFilter,
  setDeliverableFilter,
  isShortlistedFilter,
  setIsShortlistedFilter,
  onOpenReachOut,
  refetch,
  sortBy,
  setSortBy,
  createdAtFilter,
  setCreatedAtFilter,
  updatedAtFilter,
  setUpdatedAtFilter,
  sortByCreatedAt,
  sortByUpdatedAt,
  setDateColumnSort,
  dynamicFields,
  setDynamicField,
  briefInfluencers = [],
}: any) => {
  const { dynamicFacets } = useGetDynamicFacets(briefId || "");
  const { deliverableConfiguratorsData } = useGetDeliverableConfigurators();
  const configurators = deliverableConfiguratorsData?.data?.data || [];
  const openChangeContact = (record: IBriefInfluencerV2) => {
    if (record?.influencer?.contacts && record.influencer.contacts.length > 0) {
      setContactsModalProps({
        initialContact: record?.primaryContact || null,
        addContactOnly: true,
        visible: true,
        briefInfluencerId: record._id,
        conatcts: record.influencer.contacts,
        creatorInfo: {
          name: record.socialMedia.username,
          dp: record.socialMedia?.profilePicUrl,
          rating: record.influencer?.avgReview || 0,
        },
      });
      return;
    }

    setSelectedInfluencer(record);
  };

  const renderContactDropdown = (record: IBriefInfluencerV2) => (
    <Dropdown
      menu={{
        items: [
          {
            key: "change",
            label: "Change Contact",
            icon: <EditOutlined />,
            onClick: () => openChangeContact(record),
          },
        ],
      }}
      trigger={["click", "hover"]}
      placement="bottomRight"
    >
      <Button icon={<MoreOutlined />} type="text" />
    </Dropdown>
  );

  const { brief } = useGetBrIefById(briefId || "");
  const visibleCustomFields = useMemo(() => {
    let customFields: IFieldConfig[] = [];
    // if (userDetails?.userEntity?.type === "brand-user") {
    //   customFields =
    //     brief?.visibleBrandCustomFieldsV2?.filter(
    //       (field) => field.isSpecialField && field.isShared
    //     ) || [];
    // }

    customFields = [
      ...customFields,
      ...(brief?.dataFieldsV2?.filter((field) => field.isSpecialField) || []),
    ];

    return customFields;
  }, [brief?.dataFieldsV2]);

  const [editFieldProps, setEditFieldProps] = useState<{
    briefInfluencerId: string;
    key: string;
    value?: IDataFieldValue;
    fieldType?: CustomFieldType;
    originalValue?: IDataFieldValue;
  }>({
    briefInfluencerId: "",
    key: "",
    value: undefined,
    fieldType: undefined,
    originalValue: undefined,
  });

  const { editDataField, isEdited, isEditing } =
    useEditDataFieldV2Data(briefId);

  useEffect(() => {
    if (isEdited) {
      setEditFieldProps({
        briefInfluencerId: "",
        key: "",
        value: undefined,
        fieldType: undefined,
        originalValue: undefined,
      });
    }
  }, [isEdited]);

  const columns: ColumnsType<IBriefInfluencerV2> = [
    {
      title: "Creator",
      key: "creator",
      fixed: "left",
      width: 170,

      render: (_, record) => (
        <BriefCreatorColumn
          influencerId={record.influencerId}
          influencerName={record.influencerName}
          username={record.socialMedia?.username}
          platform={record.socialMedia?.platform}
          profilePicUrl={record.socialMedia?.profilePicUrl}
          link={record.socialMedia?.link}
          isCreatorDeclined={record.isCreatorDeclined}
          isBrandDeclined={record.isBrandDeclined}
          creatorDeclinedReason={record.creatorDeclinedReason}
          brandDeclinedReason={record.brandDeclinedReason}
        />
      ),
    },
    {
      title: "Creator contact",
      key: "contact",

      width: 220,
      filterDropdownProps: {
        destroyPopupOnHide: true,
      },
      filtered: isContactFilterActive,
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <BriefContactFilterV2
          {...props}
          value={contactFilter}
          onApply={setContactFilter}
        />
      ),
      render: (_, record) => {
        return (
          <Flex
            gap={5}
            justify="space-between"
            align="center"
            style={{ width: "100%" }}
          >
            <CreatorContactDetails
              contact={record.primaryContact}
              isContactAdded={record.isPrimaryContactAdded}
              isCompact={false}
            />
            {record.isPrimaryContactAdded
              ? renderContactDropdown(record)
              : null}
          </Flex>
        );
      },
    },
    {
      title: "Status",
      key: "isShortlisted",
      width: 140,
      filterCount: isShortlistedFilter !== undefined ? 1 : 0,
      filterDropdownProps: {
        destroyPopupOnHide: true,
      },
      filtered: isShortlistedFilter !== undefined,
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SearchSingleSelectFilter
          title="Status"
          filterCount={isShortlistedFilter !== undefined ? 1 : 0}
          options={[
            { label: "Shortlisted", value: "true" },
            { label: "Unshortlisted", value: "false" },
          ]}
          selectedValue={
            isShortlistedFilter === true
              ? "true"
              : isShortlistedFilter === false
                ? "false"
                : undefined
          }
          onApply={(val?: string) => {
            if (val === "true") {
              setIsShortlistedFilter(true);
            } else if (val === "false") {
              setIsShortlistedFilter(false);
            } else {
              setIsShortlistedFilter(undefined);
            }
          }}
          onReset={() => {
            setIsShortlistedFilter(undefined);
          }}
          confirm={props.confirm}
          close={props.close}
        />
      ),
      render: (_, record) => {
        const isShortlisted =
          record.isShortlisted ??
          record.status === BreifInfluencerV2StatusEnum.Shortlisted;
        return (
          <Tag color={isShortlisted ? "blue" : "default"}>
            {isShortlisted ? "Shortlisted" : "Unshortlisted"}
          </Tag>
        );
      },
    },
    {
      title: "Deliverables",
      key: "deliverables",
      width: 200,
      filterCount: deliverableFilter?.length || 0,
      filterDropdownProps: {
        destroyPopupOnHide: true,
      },
      filtered: !!deliverableFilter?.length,
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <PricingDeliverableFilter
          briefId={briefId || ""}
          confirm={props.confirm}
          close={props.close}
          selectedValues={deliverableFilter}
          onApply={setDeliverableFilter}
        />
      ),
      render: (_, record) => {
        if (record.isDeliverablesPending) {
          return <span> Pending</span>;
        }

        if (
          record?.deliverableV2 &&
          record.deliverableV2.purchaseItems &&
          record.deliverableV2.purchaseItems.length > 0
        ) {
          return (
            <Flex align="center" gap={2}>
             {record.deliverableV2 ? <BriefDeliverablePill
                deliverable={record.deliverableV2}
                configurators={configurators}
                showSpecialNotes
              /> : null}
            </Flex>
          );
        }

        return <span className={styles.emptyPlaceholder}>—</span>;
      },
    },
    {
      title: "Timeline",
      key: "timeline",
      width: 210,
      render: (_, record) => {
        const from = record.assignedPricingPocV2?.deadlineFrom;
        const to = record.assignedPricingPocV2?.deadlineTo;
        return (
          <Flex vertical gap={5}>
            {/* <Text type="secondary">Assigned</Text> */}
            <span className={styles.customFieldValue}>
              {from ? dayjs(from).format("MMM D, hh:mm A") : "—"} {"->"}{" "}
              {to ? dayjs(to).format("MMM D, hh:mm A") : "—"}
            </span>
            {/* <Text type="secondary">
              <Clock size={10} style={{ marginRight: 5 }} />
              Expires
            </Text> */}
            {/* <span className={styles.timelineText}></span> */}
          </Flex>
        );
      },
    },
    {
      title: "Past Cost",
      key: "pastCost",
      width: 150,
      render: (_, record) => {
        return (
          <Popover
            open={
              selectedInfluencer?._id === record._id &&
              !isFetchingInfluencerCampaignHistory
            }
            trigger={["click"]}
            placement="bottom"
            destroyTooltipOnHide
            styles={{
              body: {
                padding: 0,
              },
            }}
            content={
              <List
                dataSource={influencerCampaignHistory?.data || []}
                loading={isFetchingInfluencerCampaignHistory}
                style={{ height: 300, overflowY: "auto" }}
                renderItem={(c: IBriefInfluencerV2) => {
                  return (
                    <List.Item key={c._id} style={{ padding: 10 }}>
                      <Flex
                        gap={5}
                        align="center"
                        justify="space-between"
                        style={{ width: "100%" }}
                      >
                        <Flex vertical>
                          <Text className={styles.costDate}>
                            {dayjs(c.influencerCost?.createdAt).format(
                              "DD MMM YYYY"
                            )}
                          </Text>
                          <b style={{ fontSize: 11, color: "#1a1f36" }}>
                            {c.influencerCost?.cost
                              ? `₹${c.influencerCost?.cost}`
                              : "NA"}
                          </b>

                          {c.influencerCost?.cost ? (
                            <InrAmountWordsLine
                              value={c.influencerCost?.cost}
                              className={styles.amountInWords}
                            />
                          ) : null}

                          {/* <Text>{c.brief?.name}</Text> */}
                          {c?.deliverableV2 ? (
                            <Text
                              className={styles.costDate}
                              style={{ fontWeight: 500 }}
                            >
                              {c?.deliverableV2?.name ||
                                `Package ${c?.deliverableV2?.setNumber ? c?.deliverableV2?.setNumber : ""}`}
                            </Text>
                          ) : (
                            <Text
                              className={styles.costDate}
                              style={{ fontWeight: 500 }}
                            >
                              {c.brief?.name} (Deliverables Pending)
                            </Text>
                          )}
                        </Flex>
                      </Flex>
                    </List.Item>
                  );
                }}
              />
            }
            onOpenChange={(open) => {
              if (open) {
                setSelectedInfluencer(record);
              } else {
                setSelectedInfluencer(null);
              }
            }}
          >
            <Button
              className={styles.openProjectLink}
              type="link"
              icon={<DownOutlined />}
              size="small"
              iconPosition="end"
              loading={
                isFetchingInfluencerCampaignHistory &&
                selectedInfluencer?._id === record._id
              }
            >
              Past Cost
            </Button>
          </Popover>
        );
      },
    },

    ...visibleCustomFields.map((field) => {
      const headerText = field.displayName || field.key;
      const columnValues =
        field.type === CustomFieldType.UPLOAD
          ? []
          : briefInfluencers.map((record: IBriefInfluencerV2) => {
              const value = record.dataFields?.[field.key];
              if (field.type === "number")
                return value
                  ? (formatData(Number(value), "0.0a") as string)
                  : "-";
              if (Array.isArray(value)) return value.join(", ");
              return value ? String(value) : "-";
            });
      const { width: dynamicWidth, limit: dynamicLimit } =
        getDynamicColumnSizing(headerText, columnValues);
      const isEditingColumn = editFieldProps.key === field.key;

      const currentSort = sortBy?.sortByDynamicFields?.[field.key];
      const sortUI = field.type === "number" ? (
        <ColumnSortUI
          sortActive={!!currentSort}
          sortDirection={
            currentSort === SORT_ORDER.ASCEND
              ? "asc"
              : currentSort === SORT_ORDER.DESCEND
                ? "desc"
                : undefined
          }
          onSortChange={(dir) => {
            const newSort =
              dir === "asc"
                ? SORT_ORDER.ASCEND
                : dir === "desc"
                  ? SORT_ORDER.DESCEND
                  : undefined;

            const newSortByDynamicFields = {
              ...sortBy?.sortByDynamicFields,
            };
            if (newSort === undefined) {
              delete newSortByDynamicFields[field.key];
            } else {
              newSortByDynamicFields[field.key] = newSort;
            }

            setSortBy({
              ...sortBy,
              sortByDynamicFields: newSortByDynamicFields,
            });
          }}
        />
      ) : null;

      return {
        title: (
          <TextWithTooltip
            text={headerText}
            textClassName={styles.metricsHeader}
          />
        ),
        dataIndex: field.key,
        key: field.key,
        width: isEditingColumn ? Math.max(200, dynamicWidth) : dynamicWidth,
        filterCount:
          ((dynamicFields?.[field.key] as { equalTo?: string[] })?.equalTo?.length || 0) +
          ((dynamicFields?.[field.key] as { range?: { from?: number; to?: number } })?.range?.from !== undefined ||
          (dynamicFields?.[field.key] as { range?: { from?: number; to?: number } })?.range?.to !== undefined
            ? 1
            : 0) +
          (sortBy?.key === field.key ? 1 : 0),
        filterDropdownProps: {
          destroyPopupOnHide: true,
        },
        filtered: !!(
          (dynamicFields?.[field.key] as { equalTo?: string[] })?.equalTo?.length ||
          (dynamicFields?.[field.key] as { range?: { from?: number; to?: number } })?.range?.from !== undefined ||
          (dynamicFields?.[field.key] as { range?: { from?: number; to?: number } })?.range?.to !== undefined ||
          sortBy?.key === field.key
        ),
        filterDropdown: (props: { confirm: () => void; close: () => void }) => {
          if (field.type === "string" || field.type === "dropdown") {
            return (
              <SearchMultiSelectFilter
                title={headerText}
                confirm={props.confirm}
                close={props.close}
                options={(dynamicFacets?.[field.key] || []).map((item) => ({
                  label: item._id,
                  value: item._id,
                }))}
                selectedValues={
                  (
                    dynamicFields?.[field.key] as {
                      equalTo: string[];
                    }
                  )?.equalTo || []
                }
                onApply={(value) => {
                  setDynamicField(field.key, value);
                }}
                searchPlaceholder={`Search ${field.displayName || field.key}`}
              />
            );
          }

          if (field.type === "number") {
            const currentRange =
              (
                dynamicFields?.[field.key] as {
                  range: { from?: number; to?: number };
                }
              )?.range || {};

            return (
              <NumberRangeFilter
                title={headerText}
                before={sortUI}
                props={props}
                placeholder={`Select ${field.displayName} range`}
                selectedRange={currentRange}
                handleRangeChange={(range) => {
                  setDynamicField(field.key, {
                    from: range.from || 0,
                    to: range.to || 0,
                  });
                }}
              />
            );
          }

          return null;
        },
        render: (_: unknown, record: IBriefInfluencerV2) => {
          const value = record.dataFields?.[field.key];
          const isEditingField =
            editFieldProps.key === field.key &&
            record._id === editFieldProps.briefInfluencerId;

          if (isEditingField) {
            const handleSave = () => {
              editDataField({
                briefInfluencerId: record._id,
                dataField: {
                  [editFieldProps.key]: editFieldProps.value || "",
                },
                handleSuccess: (id, briefInfluencer) => {
                  refetch();
                },
              });
            };

            const handleCancel = () => {
              setEditFieldProps({
                briefInfluencerId: "",
                key: "",
                value: undefined,
                fieldType: undefined,
                originalValue: undefined,
              });
            };

            const suffixActions = (
              <Space size={2}>
                <Button
                  icon={<Check size={10} />}
                  type="text"
                  size="small"
                  loading={isEditing}
                  onClick={handleSave}
                  className={styles.editActionButton}
                />
                <Button
                  icon={<Close size={10} />}
                  type="text"
                  size="small"
                  onClick={handleCancel}
                  className={styles.editActionButton}
                />
              </Space>
            );

            if (field.type === CustomFieldType.DROPDOWN) {
              const dropdownOptions =
                field.dropdownOptions?.map((opt) => ({
                  label: opt,
                  value: opt,
                })) || [];
              const currentValue = Array.isArray(editFieldProps.value)
                ? editFieldProps.value
                : editFieldProps.value
                  ? [String(editFieldProps.value)]
                  : [];

              return (
                <SelectButton
                  mode="multiple"
                  value={currentValue}
                  onChange={(val) => {
                    // When Apply is clicked, this is called with final value
                    // When Clear/Cancel is clicked, this is called with empty array
                    const newValue = Array.isArray(val) ? val : [];

                    // Check if this is a cancel (empty value when original had value)
                    const originalValue = editFieldProps.originalValue;
                    const wasOriginalEmpty =
                      !originalValue ||
                      (Array.isArray(originalValue) &&
                        originalValue.length === 0) ||
                      originalValue === "";

                    if (newValue.length === 0 && !wasOriginalEmpty) {
                      // Cancel was clicked - cancel edit mode
                      handleCancel();
                    } else {
                      // Apply was clicked - save the value
                      editDataField({
                        briefInfluencerId: record._id,
                        dataField: {
                          [editFieldProps.key]: newValue,
                        },
                        handleSuccess: (id, briefInfluencer) => {
                          refetch();
                        },
                      });
                    }
                  }}
                  options={dropdownOptions}
                  placeholder="Select..."
                  size="small"
                  withFooterActions
                  applyText="Apply"
                  clearText="Cancel"
                  className={styles.customFieldSelect}
                />
              );
            } else if (field.type === "number") {
              return (
                <InputNumber
                  value={
                    typeof editFieldProps.value === "number"
                      ? editFieldProps.value
                      : editFieldProps.value
                        ? Number(editFieldProps.value)
                        : undefined
                  }
                  onChange={(val) => {
                    setEditFieldProps({
                      ...editFieldProps,
                      value: val || 0,
                    });
                  }}
                  onPressEnter={handleSave}
                  size="small"
                  className={styles.customFieldInput}
                  addonAfter={suffixActions}
                  autoFocus
                />
              );
            } else {
              return (
                <Input
                  value={String(editFieldProps.value || "")}
                  onChange={(e) => {
                    setEditFieldProps({
                      ...editFieldProps,
                      value: e.target.value,
                    });
                  }}
                  onPressEnter={handleSave}
                  size="small"
                  className={styles.customFieldInput}
                  suffix={suffixActions}
                  autoFocus
                />
              );
            }
          }

          // Not editing: show value and edit icon on hover
          if (field.type === CustomFieldType.UPLOAD) {
            const uploadItems: UploadItem[] =
              Array.isArray(value) &&
              value.every(
                (item) =>
                  typeof item === "object" &&
                  item !== null &&
                  "url" in item &&
                  "type" in item
              )
                ? (value as UploadItem[])
                : [];

            return (
              <UploadCell
                fieldKey={field.key}
                briefInfluencerId={record._id}
                value={uploadItems}
                onChange={(newItems) => {
                  // Directly save to backend without edit mode
                  editDataField({
                    briefInfluencerId: record._id,
                    dataField: {
                      [field.key]: newItems,
                    },
                    handleSuccess: (id, briefInfluencer) => {
                      refetch();
                    },
                  });
                }}
              />
            );
          }

          const displayValue: string =
            field.type === "number"
              ? value
                ? (formatData(Number(value), "0.0a") as string)
                : "-"
              : Array.isArray(value)
                ? value.join(", ")
                : value
                  ? String(value)
                  : "-";

          return (
            <div
              className={styles.editableCell}
              onClick={(e) => {
                e.stopPropagation();
                setEditFieldProps({
                  briefInfluencerId: record._id,
                  key: field.key,
                  value: value as string | number | string[] | undefined,
                  fieldType: field.type as CustomFieldType,
                  originalValue: value as
                    | string
                    | number
                    | string[]
                    | undefined,
                });
              }}
            >
              <TextWithTooltip
                text={displayValue}
                limit={dynamicLimit}
                textClassName={styles.customFieldValue}
              />
            </div>
          );
        },
      };
    }),

    {
      title: "Reachout",
      key: "whatsappReachoutStatus",
      width: 168,
      filterCount: reachoutStatusFilter?.length || 0,
      filterDropdownProps: {
        destroyPopupOnHide: true,
      },
      filtered: !!reachoutStatusFilter?.length,

      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SearchMultiSelectFilter
          confirm={props.confirm}
          close={props.close}
          options={WHATSAPP_REACHOUT_STATUS_FILTER_OPTIONS.map((option) => ({
            label: option.name,
            value: option.value,
          }))}
          selectedValues={reachoutStatusFilter}
          onApply={setReachoutStatusFilter}
          searchPlaceholder="Search reachout status"
        />
      ),
      render: (_, record) => {
        const canReachOut =
          record.isPrimaryContactAdded &&
          !record.isCreatorOnboardPending &&
          !record.isCreatorDeclined &&
          !record.isBrandDeclined;

        return (
          <PricingReachoutCell
            status={record.pricingWhatsappReachout?.status}
            failedReason={record.pricingWhatsappReachout?.failedReason}
            deeplinkUrl={record.pricingWhatsappReachout?.deeplinkUrl}
            onReachout={
              canReachOut
                ? (isResend) => onOpenReachOut(record, isResend)
                : undefined
            }
          />
        );
      },
    },

    {
      title: "Created at",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      filterCount: (sortByCreatedAt ? 1 : 0) + (createdAtFilter?.from || createdAtFilter?.to ? 1 : 0),
      filterDropdownProps: {
        destroyPopupOnHide: true,
      },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => {
        const sortUI = (
          <ColumnSortUI
            sortActive={!!sortByCreatedAt}
            sortDirection={
              sortByCreatedAt === SORT_ORDER.ASCEND
                ? "asc"
                : sortByCreatedAt === SORT_ORDER.DESCEND
                  ? "desc"
                  : undefined
            }
            onSortChange={(dir) => {
              const newSort =
                dir === "asc"
                  ? SORT_ORDER.ASCEND
                  : dir === "desc"
                    ? SORT_ORDER.DESCEND
                    : undefined;
              setDateColumnSort({
                sortByCreatedAt: newSort,
                sortByUpdatedAt: undefined,
              });
            }}
            sortAscLabel="Earliest first"
            sortDescLabel="Latest first"
          />
        );
        return (
          <DateRangeFilter
            title="Created at"
            before={sortUI}
            props={props}
            placeholder="Created at"
            selectedRange={createdAtFilter}
            handlePropsChange={(changeProps) => {
              setCreatedAtFilter({
                from: changeProps.range?.from,
                to: changeProps.range?.to,
              });
            }}
          />
        );
      },
      render: (createdAt: string) => (
        <span>
          {createdAt ? format(new Date(createdAt), "dd/MM/yyyy") : "—"}
        </span>
      ),
    },
    {
      title: "Updated at",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 120,
      filterCount: (sortByUpdatedAt ? 1 : 0) + (updatedAtFilter?.from || updatedAtFilter?.to ? 1 : 0),
      filterDropdownProps: {
        destroyPopupOnHide: true,
      },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => {
        const sortUI = (
          <ColumnSortUI
            sortActive={!!sortByUpdatedAt}
            sortDirection={
              sortByUpdatedAt === SORT_ORDER.ASCEND
                ? "asc"
                : sortByUpdatedAt === SORT_ORDER.DESCEND
                  ? "desc"
                  : undefined
            }
            onSortChange={(dir) => {
              const newSort =
                dir === "asc"
                  ? SORT_ORDER.ASCEND
                  : dir === "desc"
                    ? SORT_ORDER.DESCEND
                    : undefined;
              setDateColumnSort({
                sortByCreatedAt: undefined,
                sortByUpdatedAt: newSort,
              });
            }}
            sortAscLabel="Earliest first"
            sortDescLabel="Latest first"
          />
        );
        return (
          <DateRangeFilter
            title="Updated at"
            before={sortUI}
            props={props}
            placeholder="Updated at"
            selectedRange={updatedAtFilter}
            handlePropsChange={(changeProps) => {
              setUpdatedAtFilter({
                from: changeProps.range?.from,
                to: changeProps.range?.to,
              });
            }}
          />
        );
      },
      render: (updatedAt: string) => (
        <span>
          {updatedAt ? format(new Date(updatedAt), "dd/MM/yyyy") : "—"}
        </span>
      ),
    },
    {
      title: "Creator Notes",
      key: "notes",
      width: 200,
      fixed: "right",
      render: (text, record) => {
        const isEditing =
          openPocForm?.id === record._id || !record.pricingNotes;
        if (isEditing) {
          return (
            <PricingPocNotes
              notes={record.pricingNotes || ""}
              key={record._id}
              briefInfluencerId={record._id}
              handleSuccess={() => {
                queryClient.invalidateQueries({
                  queryKey: [...BRIEF_INFLUENCER_V2_SEARCH_QUERY_KEY],
                });
                setOpenPocForm(null);
              }}
              handleGoBack={() => setOpenPocForm(null)}
              showGoBackButton={!record.pricingNotes ? false : true}
            />
          );
        }

        return (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setOpenPocForm({
                id: record._id,
                notes: record.pricingNotes || "",
              });
            }}
            style={{
              cursor: "pointer",
              width: "100%",
              minHeight: "24px",
              display: "flex",
              alignItems: "center",
            }}
          >
            {record.pricingNotes ? (
              <pre className={styles.pricingNotes} style={{ margin: 0 }}>
                {record.pricingNotes}
              </pre>
            ) : (
              "-"
            )}
          </div>
        );
      },
    },
    {
      title: "Creator Commercials",
      key: "creatorCommercials",
      width: 200,
      fixed: "right",
      render: (_, record) => (
        <PricingCreatorCommercialsCell
          record={record}
          briefId={briefId}
          userId={userId}
        />
      ),
    },
    {
      title: "Notes",
      key: "briefNotes",
      width: 168,
      fixed: "right",
      render: (_, record: IBriefInfluencerV2) => (
        <NotesCell record={record} refetch={refetch} />
      ),
    },
  ];

  return columns;
};
